// ./src/hooks/useInventoryItems.ts
//
// Catalog hook for the account's inventory items (stock-tracked supplies/gear).
// Mirrors useRentalAssets: server pagination + load-more, race-guarded list, a
// concurrent-removal Set, and an optional eventId threaded as contextEventId (not
// stored) so the catalog can be managed from an event page. See em-scaffold-ui.
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import {
  InventoryItem,
  InventoryItemService,
  InventoryItemFilter,
  InventoryItemInput,
  Pagination,
  SortItem,
  listPage,
} from "@/utils/api";
import { upsertBy, upsertManyBy, removeBy, removeManyBy } from "@/utils";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

// ============================================
// Types
// ============================================

export interface UseInventoryItemsOptions {
  pageSize?: number;
  initialFilters?: Partial<InventoryItemFilter>;
  initialSort?: SortItem[];
  autoLoad?: boolean;
  /**
   * When the catalog is managed from an EVENT page (the event Inventory page),
   * passing the eventId lets crew with event.operate on it read/manage the event
   * account's items. Threaded as `contextEventId` (not stored) into every op.
   */
  eventId?: string | null;
}

export interface UseInventoryItemsReturn {
  inventoryItem: InventoryItem | null;
  inventoryItems: InventoryItem[];
  loading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;

  isCreating: boolean;
  isUpdating: boolean;
  isBulkUpdating: boolean;
  removingIds: Set<string>;
  isRemoving: (id?: string) => boolean;

  filters: Partial<InventoryItemFilter>;
  sortBy: SortItem[];

  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<InventoryItemFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  create: (params: InventoryItemInput) => Promise<OpResult>;
  update: (id: string, params: InventoryItemInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;

  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  reset: () => void;
  getById: (id: string | undefined) => InventoryItem | undefined;
  /** One-off server search for pickers (account/event-scoped), independent of the list state. */
  searchItems: (term: string) => Promise<InventoryItem[]>;

  totalCount: number;

  lastError: OpResult | null;
  clearError: () => void;
}

// ============================================
// Defaults
// ============================================

const DEFAULT_SORT: SortItem[] = [
  { name: "orderNo", order: "ASC" },
  { name: "name", order: "ASC" },
];

const DEFAULT_FILTERS: Partial<InventoryItemFilter> = {};

// ============================================
// Hook
// ============================================

export const useInventoryItems = (options: UseInventoryItemsOptions = {}): UseInventoryItemsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    eventId = null,
  } = options;

  const { user } = useApp();
  const userId = user?.id ?? null;

  const pageSizeRef = useRef(pageSize);
  // The page size of the LAST read: a refresh re-runs that read, so a WHOLE_SET read stays whole (#118).
  const lastPageSizeRef = useRef<number>(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);
  const eventIdRef = useRef<string | null>(eventId);
  eventIdRef.current = eventId;

  const lastQueriedFilterRef = useRef<Partial<InventoryItemFilter>>(initialFilters);
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  const listCallIdRef = useRef(0);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  const [inventoryItem, setInventoryItem] = useState<InventoryItem | null>(null);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state — "No notes
   * yet", "No tasks yet" — before the skeleton and then the data. Reported across the whole event
   * space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Server total for the current filter; hasMore is derived from it (never a page-full heuristic).
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState<Partial<InventoryItemFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  const [currentPage, setCurrentPage] = useState(1);

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isBulkUpdating] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());

  const [lastError, setLastError] = useState<OpResult | null>(null);

  const isEmpty = useMemo(() => isLoaded && inventoryItems.length === 0, [isLoaded, inventoryItems.length]);
  const hasMore = inventoryItems.length < total;

  const startRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));

      return next;
    });
  }, []);

  const stopRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));

      return next;
    });
  }, []);

  const isRemoving = useCallback(
    (id?: string): boolean => (id ? removingIds.has(id) : removingIds.size > 0),
    [removingIds]
  );

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const reset = useCallback(() => {
    listCallIdRef.current++;

    setInventoryItem(null);
    setInventoryItems([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setTotal(0);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingIds(new Set());
  }, []);

  const get = useCallback(async (id: string): Promise<OpResult> => {
    setLoading(true);
    setLastError(null);

    try {
      const result = await InventoryItemService.get(id);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = (result.data as InventoryItem[])[0];
        setInventoryItem(data);
        setInventoryItems((prev) => upsertBy(prev, data));
        setLastError(null);

        return result;
      }

      setLastError(result);
      setInventoryItem(null);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);
      setInventoryItem(null);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  const list = useCallback(
    async (
      filterOverride: Partial<InventoryItemFilter> = {},
      pagination?: Pagination,
      sortByOverride?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      lastPageSizeRef.current = targetPageSize;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
        setLoadingMore(false);
        setCurrentPage(1);
      }

      setLastError(null);

      setFilters(filterOverride);
      lastQueriedFilterRef.current = filterOverride;

      if (sortByOverride) {
        setSortBy(sortByOverride);
        lastQueriedSortRef.current = sortByOverride;
      }

      const currentSortBy = sortByOverride || lastQueriedSortRef.current;

      try {
        const result = await listPage(
          InventoryItemService.list,
          {
            ...(filterOverride as InventoryItemFilter),
            ...(eventIdRef.current ? { contextEventId: eventIdRef.current } : {}),
          },
          { page: targetPage, pageSize: targetPageSize },
          currentSortBy
        );

        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data) ? (result.data as InventoryItem[]) : [];

          if (isAppending) {
            setInventoryItems((prev) => upsertManyBy(prev, newItems, "id", "end"));
          } else {
            setInventoryItems(newItems);
          }

          if (typeof result.total === "number") {
            setTotal(result.total);
          } else if (!isAppending) {
            setTotal(newItems.length);
          }

          setCurrentPage(targetPage);
          setIsLoaded(true);
          setLastError(null);

          return result;
        }

        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setInventoryItems([]);
        }

        return result;
      } catch (error) {
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setInventoryItems([]);
        }

        return result;
      } finally {
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  const create = useCallback(async (params: InventoryItemInput): Promise<OpResult> => {
    setIsCreating(true);
    setLastError(null);

    try {
      const result = await InventoryItemService.create({
        ...params,
        ...(eventIdRef.current ? { contextEventId: eventIdRef.current } : {}),
      });

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const newItem = (result.data as InventoryItem[])[0];
        setInventoryItem(newItem);
        setInventoryItems((prev) => upsertBy(prev, newItem));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsCreating(false);
    }
  }, []);

  const update = useCallback(async (id: string, params: InventoryItemInput): Promise<OpResult> => {
    if (!id) {
      const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Inventory item ID is required for updating");
      setLastError(result);

      return result;
    }

    setIsUpdating(true);
    setLastError(null);

    try {
      const result = await InventoryItemService.update(
        { id, ...(eventIdRef.current ? { contextEventId: eventIdRef.current } : {}) },
        { ...params, ...(eventIdRef.current ? { contextEventId: eventIdRef.current } : {}) }
      );

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const updatedItem = (result.data as InventoryItem[])[0];
        setInventoryItem(updatedItem);
        setInventoryItems((prev) => upsertBy(prev, updatedItem));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Inventory item ID is required for removal");
        setLastError(result);

        return result;
      }

      startRemoving([id]);
      setLastError(null);

      try {
        const result = await InventoryItemService.remove(id, eventIdRef.current);

        if (result.code === OP_RESULT_CODES.OK) {
          setInventoryItem(null);
          setInventoryItems((prev) => removeBy(prev, id));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving([id]);
      }
    },
    [startRemoving, stopRemoving]
  );

  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "At least one item must be selected");
        setLastError(result);

        return result;
      }

      startRemoving(ids);
      setLastError(null);

      try {
        const result = await InventoryItemService.removeMany(ids, eventIdRef.current);

        if (result.code === OP_RESULT_CODES.OK) {
          setInventoryItem(null);
          setInventoryItems((prev) => removeManyBy(prev, ids));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving(ids);
      }
    },
    [startRemoving, stopRemoving]
  );

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    const nextPage = currentPage + 1;

    await list(
      lastQueriedFilterRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loading, loadingMore, hasMore, currentPage]);

  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFilterRef.current,
      { page: 1, pageSize: lastPageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(refresh, isLoaded);

  const getById = useCallback(
    (id: string | undefined): InventoryItem | undefined => {
      if (!id) {
        return undefined;
      }

      return inventoryItems.find((item) => item.id === id);
    },
    [inventoryItems]
  );

  // A one-off SERVER search for pickers (InventoryItemSelect) — scoped to the same account (the
  // event's, via contextEventId, for crew) as the list, but WITHOUT touching the list state, so a
  // picker can find items beyond the loaded page. Returns the hits (empty on failure).
  const searchItems = useCallback(async (term: string): Promise<InventoryItem[]> => {
    const filter: Partial<InventoryItemFilter> = {};
    const q = (term || "").trim();

    if (q) {
      filter.searchKeyword = q;
    }

    if (eventIdRef.current) {
      filter.contextEventId = eventIdRef.current;
    }

    const result = await InventoryItemService.list(filter as InventoryItemFilter, { page: 1, pageSize: 20 }, DEFAULT_SORT);

    return result.didSucceed() ? ((result.data as InventoryItem[]) ?? []) : [];
  }, []);

  const totalCount = total;

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list(initialFiltersRef.current, { page: 1, pageSize: pageSizeRef.current }, initialSortRef.current, true);
      }

      return;
    }

    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list(initialFiltersRef.current, { page: 1, pageSize: pageSizeRef.current }, initialSortRef.current, true);
      }
    }
  }, [userId, autoLoad, list, reset]);

  return useMemo(
    () => ({
      inventoryItem,
      inventoryItems,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      pageSize: pageSizeRef.current,
      isCreating,
      isUpdating,
      isBulkUpdating,
      removingIds,
      isRemoving,
      filters,
      sortBy,
      get,
      list,
      create,
      update,
      remove,
      removeMany,
      loadMore,
      refresh,
      reset,
      getById,
      searchItems,
      totalCount,
      lastError,
      clearError,
    }),
    [
      inventoryItem,
      inventoryItems,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      isCreating,
      isUpdating,
      isBulkUpdating,
      removingIds,
      isRemoving,
      filters,
      sortBy,
      get,
      list,
      create,
      update,
      remove,
      removeMany,
      loadMore,
      refresh,
      reset,
      getById,
      searchItems,
      totalCount,
      lastError,
      clearError,
    ]
  );
};
